import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Briefcase, Calendar, Clock, DollarSign, Star, Users, Settings, LogOut, ChevronLeft, ChevronRight, Plus, X, MapPin, Edit2, CheckCircle, XCircle, FileText, Play, Link2, MessageSquare, Copy, ClipboardCheck, Video, Award, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth, API } from '../App';
import NotificationBell from '../components/NotificationBell';
import ProfileImageUpload from '../components/ProfileImage';
import { toast } from 'sonner';
import axios from 'axios';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, setHours, setMinutes, subMonths } from 'date-fns';
import TrainingContract from '../components/TrainingContract';
import { REGIONS, LANGUAGES, SKILLS, INDUSTRIES, getLanguageForCountry, getMainCategories, getSubCategories, getSubSubCategories, getTrainingTypes } from '../data/globalData';
import { useCurrency } from '../components/CurrencyProvider';

const BADGE_STYLES = {
  'Top Rated': 'bg-amber-100 text-amber-800 border border-amber-200',
  'Elite Trainer': 'bg-purple-100 text-purple-800 border border-purple-200',
  'Experienced Trainer': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Rising Star': 'bg-pink-100 text-pink-800 border border-pink-200',
  'Verified Profile': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  'Highly Rated': 'bg-orange-100 text-orange-800 border border-orange-200',
};
const TRAINING_STATUS_STYLES = {
  completed: 'bg-emerald-50 text-emerald-700',
  in_progress: 'bg-blue-50 text-blue-700',
  feedback_pending: 'bg-purple-50 text-purple-700',
};
const TRAINING_STATUS_LABELS = {
  completed: 'Completed',
  in_progress: 'In Progress',
  feedback_pending: 'Feedback Pending',
};
const getTrainingStatusStyle = (s) => TRAINING_STATUS_STYLES[s] || 'bg-amber-50 text-amber-700';
const getTrainingStatusLabel = (s) => TRAINING_STATUS_LABELS[s] || 'Confirmed';

const INTERVIEW_STATUS_STYLES = {
  accepted: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-600',
};
const getInterviewStatusStyle = (s) => INTERVIEW_STATUS_STYLES[s] || 'bg-amber-100 text-amber-800';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

export default function TrainerDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout } = useAuth();
  const { format: formatCurrency } = useCurrency();
  
  // 3-tier training selection for profile
  const [trainingMainCat, setTrainingMainCat] = useState('');
  const [trainingSubCat, setTrainingSubCat] = useState('');
  const [trainingSubSubCat, setTrainingSubSubCat] = useState('');
  
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({ monthly: [], skills: [] });
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [slotForm, setSlotForm] = useState({ day_of_week: 0, start_time: '09:00', end_time: '17:00' });
  const [loading, setLoading] = useState(true);
  const [contractBooking, setContractBooking] = useState(null);
  const [activeTab, setActiveTab] = useState('recommended');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [editingProfile, setEditingProfile] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [trainingRecords, setTrainingRecords] = useState(null);

  const getBadgeStyle = (badge) => {
    const styles = {
      'Top Rated': 'bg-amber-100 text-amber-800',
      'Expert': 'bg-blue-100 text-blue-800',
      'Rising Star': 'bg-purple-100 text-purple-800',
      'Veteran': 'bg-emerald-100 text-emerald-800',
    };
    return styles[badge] || 'bg-slate-100 text-slate-700';
  };
  
  const [profileForm, setProfileForm] = useState({
    bio: '',
    skills: [],
    qualifications: [],
    certifications: [],
    experience_years: 0,
    hourly_rate: 0,
    location: '',
    industry: [],
    training_types: [],
    languages: []
  });

  // Get cities for selected country
  const getCitiesForCountry = (country) => {
    if (!country) return [];
    for (const [region, countries] of Object.entries(REGIONS)) {
      if (countries[country]) {
        return countries[country];
      }
    }
    return [];
  };

  // Get all countries grouped by region
  const getCountriesByRegion = () => {
    const result = {};
    Object.entries(REGIONS).forEach(([region, countries]) => {
      result[region] = Object.keys(countries);
    });
    return result;
  };

  // Find country for a given city
  const findCountryForCity = (city) => {
    for (const [region, countries] of Object.entries(REGIONS)) {
      for (const [country, cities] of Object.entries(countries)) {
        if (cities.includes(city)) {
          return country;
        }
      }
    }
    return '';
  };

  // Handle country change with auto-language
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    const autoLanguage = getLanguageForCountry(country);
    setProfileForm(prev => ({
      ...prev,
      location: '',
      languages: prev.languages.includes(autoLanguage) ? prev.languages : [autoLanguage, ...prev.languages]
    }));
  };

  const [eventForm, setEventForm] = useState({
    title: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    event_type: 'booked'
  });

  const fetchData = useCallback(async () => {
    if (!user || !token) return;
    
    try {
      // Fetch profile
      const profileRes = await axios.get(`${API}/trainers/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(profileRes.data);
      setProfileForm({
        bio: profileRes.data.bio || '',
        skills: profileRes.data.skills || [],
        qualifications: profileRes.data.qualifications || [],
        certifications: profileRes.data.certifications || [],
        experience_years: profileRes.data.experience_years || 0,
        hourly_rate: profileRes.data.hourly_rate || 0,
        location: profileRes.data.location || '',
        industry: profileRes.data.industry || [],
        training_types: (profileRes.data.training_types || []).filter(t => t && t.trim()),
        languages: profileRes.data.languages || []
      });
      
      // Set the country based on the saved location
      if (profileRes.data.location) {
        const country = findCountryForCity(profileRes.data.location);
        setSelectedCountry(country);
      }

      // Fetch stats
      const statsRes = await axios.get(`${API}/stats/trainer/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Fetch applications
      const appsRes = await axios.get(`${API}/applications/trainer/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(appsRes.data);

      // Fetch bookings
      const bookingsRes = await axios.get(`${API}/bookings/trainer/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(bookingsRes.data);

      // Compute analytics
      const monthlyMap = {};
      const last6 = [];
      for (let i = 5; i >= 0; i--) {
        const d = subMonths(new Date(), i);
        const key = format(d, 'MMM yyyy');
        monthlyMap[key] = { month: format(d, 'MMM'), bookings: 0, earnings: 0 };
        last6.push(key);
      }
      bookingsRes.data.forEach(b => {
        const key = format(new Date(b.start_datetime), 'MMM yyyy');
        if (monthlyMap[key]) {
          monthlyMap[key].bookings += 1;
          monthlyMap[key].earnings += b.amount || 0;
        }
      });
      const skillCounts = {};
      (profileRes.data.skills || []).forEach(s => { skillCounts[s] = (skillCounts[s] || 0) + 1; });
      const appliedJobs = await axios.get(`${API}/applications/trainer/${profileRes.data.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setAnalyticsData({
        monthly: last6.map(k => monthlyMap[k]),
        skills: Object.entries(skillCounts).map(([name, value]) => ({ name, value })),
        totalApplications: appliedJobs.data?.length || 0
      });

      // Fetch calendar
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      const calendarRes = await axios.get(
        `${API}/calendar/${profileRes.data.id}?start=${weekStart.toISOString()}&end=${weekEnd.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalendarEvents(calendarRes.data);

      // Fetch matched jobs for trainer
      try {
        const matchedJobsRes = await axios.get(`${API}/jobs/matched/${profileRes.data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMatchedJobs(matchedJobsRes.data);
      } catch (err) {
        console.log('No matched jobs found');
      }

      // Fetch interviews for trainer
      try {
        const interviewsRes = await axios.get(`${API}/interviews/trainer/${profileRes.data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInterviews(interviewsRes.data);
      } catch (err) {
        console.log('No interviews found');
      }

      // Fetch availability slots
      try {
        const slotsRes = await axios.get(`${API}/availability/${profileRes.data.id}`);
        setAvailabilitySlots(slotsRes.data);
      } catch (err) {
        console.log('No availability slots');
      }

      // Fetch portfolio items
      try {
        const portfolioRes = await axios.get(`${API}/portfolio/${profileRes.data.id}`);
        setPortfolioItems(portfolioRes.data);
      } catch (err) {
        console.log('No portfolio items');
      }

      // Fetch training records
      try {
        const recordsRes = await axios.get(`${API}/training-records/${profileRes.data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrainingRecords(recordsRes.data);
      } catch (err) {
        console.log('No training records');
      }

      // Fetch notification preferences
      try {
        const prefsRes = await axios.get(`${API}/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifPrefs(prefsRes.data);
      } catch (err) {
        console.log('No notification preferences');
      }

      // Fetch unread message count
      try {
        const unreadRes = await axios.get(`${API}/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadMsgCount(unreadRes.data.unread_count);
      } catch (err) {
        console.log('No unread count');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user, token, currentWeek]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle tab from URL query params (e.g., from notification links)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);


  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleAddAvailabilitySlot = async () => {
    try {
      await axios.post(`${API}/availability`, slotForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Availability slot added!');
      setShowAddSlot(false);
      setSlotForm({ day_of_week: 0, start_time: '09:00', end_time: '17:00' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/availability/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slot removed');
      fetchData();
    } catch {
      toast.error('Failed to remove slot');
    }
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const title = window.prompt('Title for this portfolio item:', file.name.split('.')[0]);
    if (!title) return;
    const itemType = file.type.startsWith('image/') ? 'sample' : file.name.toLowerCase().includes('cert') ? 'certificate' : 'document';
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/portfolio/upload?title=${encodeURIComponent(title)}&item_type=${itemType}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Portfolio item uploaded!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDeletePortfolioItem = async (itemId) => {
    try {
      await axios.delete(`${API}/portfolio/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Item removed');
      fetchData();
    } catch { toast.error('Failed to remove item'); }
  };

  const handleSaveNotifPrefs = async () => {
    try {
      const res = await axios.put(`${API}/notification-preferences`, notifPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifPrefs(res.data);
      toast.success('Notification preferences saved!');
    } catch (err) { toast.error('Failed to save preferences'); }
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/trainers/${profile.id}`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully!');
      setEditingProfile(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleAddEvent = async () => {
    try {
      const startDateTime = `${eventForm.date}T${eventForm.start_time}:00`;
      const endDateTime = `${eventForm.date}T${eventForm.end_time}:00`;
      
      await axios.post(`${API}/calendar`, {
        title: eventForm.title || (eventForm.event_type === 'available' ? 'Available' : 'Booked'),
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        event_type: eventForm.event_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Calendar event added!');
      setAddingEvent(false);
      setEventForm({
        title: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '17:00',
        event_type: 'booked'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/calendar/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const toggleSkill = (skill) => {
    setProfileForm(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const toggleIndustry = (ind) => {
    setProfileForm(prev => ({
      ...prev,
      industry: prev.industry.includes(ind)
        ? prev.industry.filter(i => i !== ind)
        : [...prev.industry, ind]
    }));
  };

  const toggleTrainingType = (type) => {
    setProfileForm(prev => ({
      ...prev,
      training_types: prev.training_types.includes(type)
        ? prev.training_types.filter(t => t !== type)
        : [...prev.training_types, type]
    }));
  };

  const toggleLanguage = (lang) => {
    setProfileForm(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from({ length: 12 }, (_, i) => i + 8);

  const getEventsForDay = (date) => {
    const dayOfWeek = (date.getDay() + 6) % 7; // Convert to Mon=0, Sun=6
    
    // Get calendar events (one-off entries + bookings)
    const calEvents = calendarEvents.filter(event => {
      const eventDate = parseISO(event.start_datetime);
      return isSameDay(eventDate, date);
    });

    // Also show confirmed bookings on calendar
    const bookingEvents = bookings
      .filter(b => b.status === 'confirmed' || b.status === 'pending_approval')
      .filter(b => {
        try {
          const bStart = parseISO(b.start_datetime);
          const bEnd = parseISO(b.end_datetime);
          // Show booking on any day within the date range
          return date >= new Date(bStart.getFullYear(), bStart.getMonth(), bStart.getDate()) &&
                 date <= new Date(bEnd.getFullYear(), bEnd.getMonth(), bEnd.getDate());
        } catch { return false; }
      })
      .map(b => {
        const bStart = parseISO(b.start_datetime);
        const startHour = isSameDay(bStart, date) ? bStart.getHours() : 9;
        return {
          id: `booking-${b.id}-${format(date, 'yyyy-MM-dd')}`,
          title: `${b.job_title} (${b.corporate_name})`,
          start_datetime: setHours(date, startHour).toISOString(),
          event_type: b.status === 'pending_approval' ? 'personal' : 'booked',
          is_booking: true,
          booking_status: b.status
        };
      })
      // Avoid duplicates if calendar event already exists for this booking
      .filter(be => !calEvents.some(ce => ce.booking_id && be.id.includes(ce.booking_id)));

    // Overlay recurring availability slots as green events
    const slotEvents = availabilitySlots
      .filter(slot => slot.day_of_week === dayOfWeek)
      .map(slot => {
        const startHour = parseInt(slot.start_time.split(':')[0]);
        const startMin = parseInt(slot.start_time.split(':')[1] || '0');
        const endHour = parseInt(slot.end_time.split(':')[0]);
        const endMin = parseInt(slot.end_time.split(':')[1] || '0');
        // Create one event per hour block in the range
        const events = [];
        for (let h = startHour; h < endHour || (h === endHour && endMin > 0); h++) {
          const startDt = setMinutes(setHours(date, h), startMin && h === startHour ? startMin : 0);
          events.push({
            id: `avail-${slot.id}-${h}`,
            title: 'Available',
            start_datetime: startDt.toISOString(),
            event_type: 'available',
            is_recurring: true
          });
        }
        return events;
      })
      .flat();

    return [...slotEvents, ...calEvents, ...bookingEvents];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="trainer-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/search?type=jobs')}
                className="text-slate-600"
                data-testid="browse-jobs-btn"
              >
                Browse Requirements
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/messages')}
                className="text-slate-600 relative"
                data-testid="messages-link"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Messages
                {unreadMsgCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center" data-testid="unread-badge">{unreadMsgCount}</span>
                )}
              </Button>
              <NotificationBell />
              <Button
                variant="ghost"
                onClick={logout}
                className="text-slate-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-2">
            Welcome back, {profile?.name}!
          </h1>
          <p className="text-slate-500">Manage your profile, calendar, and applications</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all" onClick={() => navigate('/trainer/bookings')} data-testid="stat-bookings">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-slate-500">Total Bookings</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              {stats?.total_bookings || 0}
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-emerald-300 hover:shadow-sm transition-all" onClick={() => navigate('/trainer/earnings')} data-testid="stat-earnings">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-slate-500">Total Earnings</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              ₹{(stats?.total_earnings || 0).toLocaleString()}
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-amber-300 hover:shadow-sm transition-all" onClick={() => navigate('/trainer/ratings')} data-testid="stat-rating">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-slate-500">Rating</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              {stats?.rating?.toFixed(1) || '0.0'}
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-purple-300 hover:shadow-sm transition-all" onClick={() => navigate('/trainer/applications')} data-testid="stat-pending-apps">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-500">Pending Apps</span>
            </div>
            <div className="font-outfit text-3xl font-bold text-slate-900">
              {stats?.pending_applications || 0}
            </div>
          </div>
        </div>

        {/* Badges */}
        {stats?.badges && stats.badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="trainer-badges">
            {stats.badges.map((badge) => (
              <span
                key={badge}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${getBadgeStyle(badge)}`}
              >
                <Award className="w-3.5 h-3.5" />
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg flex-wrap" id="trainer-tabs">
            <TabsTrigger value="recommended" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-recommended">
              <Briefcase className="w-4 h-4 mr-2" />
              Jobs ({matchedJobs.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-bookings">
              Bookings ({bookings.filter(b => b.status === 'confirmed').length} confirmed / {bookings.filter(b => b.status === 'pending_approval').length} pending)
            </TabsTrigger>
            <TabsTrigger value="training-records" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-training-records">
              <CheckCircle className="w-4 h-4 mr-1" />
              Training Records
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-calendar">
              Calendar
            </TabsTrigger>
            <TabsTrigger value="applications" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-applications">
              Applications ({applications.length})
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-profile">
              Profile
            </TabsTrigger>
            <TabsTrigger value="interviews" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-interviews">
              <Video className="w-4 h-4 mr-1" />
              Interviews ({interviews.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-1" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="availability" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-availability">
              <Clock className="w-4 h-4 mr-1" />
              Availability
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-portfolio">
              <FileText className="w-4 h-4 mr-1" />
              Portfolio ({portfolioItems.length})
            </TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-notifications">
              <Settings className="w-4 h-4 mr-1" />
              Alert Preferences
            </TabsTrigger>
          </TabsList>

          {/* Recommended Jobs Tab */}
          <TabsContent value="recommended">
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search jobs by title, skill, location..."
                    value={jobSearchQuery}
                    onChange={(e) => setJobSearchQuery(e.target.value)}
                    className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
                    data-testid="job-search-input"
                  />
                </div>
                <Button onClick={() => navigate('/search?type=jobs')} variant="outline" className="shrink-0" data-testid="browse-all-jobs-btn">
                  Browse All
                </Button>
              </div>

              {(() => {
                const filteredJobs = jobSearchQuery
                  ? matchedJobs.filter(job =>
                      job.title?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
                      job.description?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
                      job.location?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
                      job.corporate_name?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
                      job.skills_required?.some(s => s.toLowerCase().includes(jobSearchQuery.toLowerCase()))
                    )
                  : matchedJobs;

                return filteredJobs.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No matching requirements yet</h3>
                  <p className="text-slate-500 mb-6">Complete your profile to see jobs matching your skills</p>
                  <Button onClick={() => navigate('/search?type=jobs')} className="bg-blue-600 hover:bg-blue-700 text-white">
                    Browse All Jobs
                  </Button>
                </div>
              ) : (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-800">
                        {filteredJobs.length} jobs match your profile{jobSearchQuery ? ` for "${jobSearchQuery}"` : ''}!
                      </span>
                    </div>
                    <p className="text-emerald-700 text-sm mt-1">
                      These opportunities are tailored based on your skills, experience, and preferences.
                    </p>
                  </div>
                  {filteredJobs.map(job => (
                    <div 
                      key={job.id} 
                      className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-emerald-200 transition-all cursor-pointer"
                      onClick={() => navigate(`/job/${job.id}`)}
                      data-testid={`matched-job-${job.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-lg text-slate-900">{job.title}</h4>
                            {job.match_score >= 30 && (
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                                Great Match
                              </span>
                            )}
                          </div>
                          <p className="text-slate-500 text-sm">{job.corporate_name}</p>
                        </div>
                        <span className="font-semibold text-emerald-600 text-lg">{job.budget_currency === 'USD' ? '$' : job.budget_currency === 'EUR' ? '€' : job.budget_currency === 'GBP' ? '£' : '₹'}{job.budget?.toLocaleString()}</span>
                      </div>
                      
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">{job.description}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(job.start_date).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {job.duration_hours} hours
                        </span>
                      </div>

                      {job.skills_required?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {job.skills_required.slice(0, 4).map(skill => (
                            <span 
                              key={skill} 
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                profile?.skills?.includes(skill) 
                                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {skill}
                              {profile?.skills?.includes(skill) && ' ✓'}
                            </span>
                          ))}
                          {job.skills_required.length > 4 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                              +{job.skills_required.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              );
              })()}
            </div>
          </TabsContent>

          {/* Training Records Tab */}
          <TabsContent value="training-records">
            <div className="space-y-6" data-testid="training-records-section">
              {trainingRecords ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                      <p className="text-3xl font-bold text-emerald-600">{trainingRecords.total_completed}</p>
                      <p className="text-xs text-slate-500 mt-1">Completed Trainings</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                      <p className="text-3xl font-bold text-blue-600">₹{trainingRecords.total_earnings?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1">Total Earnings</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                      <p className="text-3xl font-bold text-amber-600">{trainingRecords.avg_feedback_rating || '—'}</p>
                      <p className="text-xs text-slate-500 mt-1">Avg Feedback Rating</p>
                    </div>
                  </div>

                  {/* Records List */}
                  {trainingRecords.records.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                      <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No completed trainings yet</h3>
                      <p className="text-slate-500">Completed training sessions will be recorded here with feedback scores.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b">
                            <th className="text-left p-4 font-medium text-slate-600">Training</th>
                            <th className="text-left p-4 font-medium text-slate-600">Company</th>
                            <th className="text-left p-4 font-medium text-slate-600">Date</th>
                            <th className="text-left p-4 font-medium text-slate-600">Duration</th>
                            <th className="text-right p-4 font-medium text-slate-600">Amount</th>
                            <th className="text-center p-4 font-medium text-slate-600">Feedback</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trainingRecords.records.map((record, idx) => (
                            <tr key={record.booking_id} className={`border-b last:border-b-0 ${idx % 2 === 0 ? '' : 'bg-slate-50/50'}`} data-testid={`record-${record.booking_id}`}>
                              <td className="p-4">
                                <div className="font-medium text-slate-900">{record.job_title}</div>
                                <div className="text-xs text-slate-500">{record.training_type} | {record.location}</div>
                              </td>
                              <td className="p-4 text-slate-600">{record.corporate_name}</td>
                              <td className="p-4 text-slate-600">{new Date(record.start_date).toLocaleDateString()}</td>
                              <td className="p-4 text-slate-600">{record.duration_hours}h</td>
                              <td className="p-4 text-right font-medium text-emerald-600">₹{record.amount?.toLocaleString()}</td>
                              <td className="p-4 text-center">
                                {record.feedback_count > 0 ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                    <span className="font-medium">{record.avg_rating}</span>
                                    <span className="text-xs text-slate-400">({record.feedback_count})</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-xs">No feedback</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-slate-500">Loading training records...</div>
              )}
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            {/* Upcoming Bookings Summary */}
            {bookings.filter(b => b.status === 'confirmed' || b.status === 'pending_approval').length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="upcoming-bookings-summary">
                <h4 className="font-semibold text-sm text-slate-700 mb-3 uppercase tracking-wider">Upcoming & Active Bookings</h4>
                <div className="space-y-2">
                  {bookings
                    .filter(b => b.status === 'confirmed' || b.status === 'pending_approval')
                    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
                    .map(b => {
                      const startDate = new Date(b.start_datetime);
                      const isPending = b.status === 'pending_approval';
                      return (
                        <div
                          key={b.id}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                            isPending ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'
                          }`}
                          onClick={() => setCurrentWeek(startDate)}
                          data-testid={`jump-booking-${b.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-8 rounded-full ${isPending ? 'bg-amber-400' : 'bg-blue-500'}`} />
                            <div>
                              <p className="font-medium text-sm text-slate-900">{b.job_title}</p>
                              <p className="text-xs text-slate-500">{b.corporate_name} | {startDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${isPending ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                              {isPending ? 'Pending' : 'Confirmed'}
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      );
                    })}
                </div>
                <p className="text-xs text-slate-400 mt-2">Click a booking to jump to that week on the calendar</p>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Calendar Header */}
              <div className="flex items-center justify-between p-4 border-b bg-slate-50">
                <h3 className="font-outfit font-semibold text-lg text-slate-900">
                  {format(weekStart, 'MMMM yyyy')}
                </h3>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={() => setCurrentWeek(new Date())}>Today</Button>
                  <Button variant="outline" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  
                  <Dialog open={addingEvent} onOpenChange={setAddingEvent}>
                    <DialogTrigger asChild>
                      <Button className="ml-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-booking-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Booking
                      </Button>
                    </DialogTrigger>
                    <DialogContent onPointerDownOutside={(e) => e.stopPropagation()} onInteractOutside={(e) => e.stopPropagation()}>
                      <DialogHeader>
                        <DialogTitle>Add a Booking</DialogTitle>
                      </DialogHeader>
                      <p className="text-sm text-slate-500 mb-2">Add an event to your training calendar.</p>
                      <div className="space-y-4">
                        <div>
                          <Label>Event Type</Label>
                          <select
                            value={eventForm.event_type}
                            onChange={(e) => setEventForm(prev => ({ ...prev, event_type: e.target.value }))}
                            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                            data-testid="event-type-select"
                          >
                            <option value="booked">Booked</option>
                            <option value="available">Available</option>
                            <option value="personal">Personal / Blocked</option>
                            <option value="travel">Travel</option>
                            <option value="preparation">Preparation</option>
                          </select>
                        </div>
                        <div>
                          <Label>Training Title</Label>
                          <Input
                            value={eventForm.title}
                            onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                            placeholder={eventForm.event_type === 'booked' ? 'e.g., Java Training - Wipro' : 'e.g., Open for bookings'}
                          />
                        </div>
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={eventForm.date}
                            onChange={(e) => setEventForm(prev => ({ ...prev, date: e.target.value }))}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Start Time</Label>
                            <Input
                              type="time"
                              value={eventForm.start_time}
                              onChange={(e) => setEventForm(prev => ({ ...prev, start_time: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>End Time</Label>
                            <Input
                              type="time"
                              value={eventForm.end_time}
                              onChange={(e) => setEventForm(prev => ({ ...prev, end_time: e.target.value }))}
                            />
                          </div>
                        </div>
                        <Button onClick={handleAddEvent} className={`w-full text-white ${eventForm.event_type === 'booked' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="confirm-add-booking">
                          {eventForm.event_type === 'booked' ? 'Add Booking' : 'Add Availability'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Day Headers */}
                  <div className="grid grid-cols-8 border-b bg-slate-50">
                    <div className="p-3 text-sm font-medium text-slate-500 border-r"></div>
                    {weekDays.map((day, i) => (
                      <div
                        key={i}
                        className={`p-3 text-center border-r last:border-r-0 ${
                          isSameDay(day, new Date()) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="text-xs text-slate-500 uppercase">{format(day, 'EEE')}</div>
                        <div className={`text-lg font-semibold ${
                          isSameDay(day, new Date()) ? 'text-blue-600' : 'text-slate-900'
                        }`}>
                          {format(day, 'd')}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Time Slots */}
                  <div className="relative">
                    {hours.map(hour => (
                      <div key={hour} className="grid grid-cols-8 border-b last:border-b-0">
                        <div className="p-2 text-xs text-slate-400 text-right border-r bg-slate-50/50">
                          {hour}:00
                        </div>
                        {weekDays.map((day, dayIndex) => {
                          const dayEvents = getEventsForDay(day);
                          const availableHours = dayEvents.filter(e => e.event_type === 'available' && parseISO(e.start_datetime).getHours() === hour);
                          const otherEvents = dayEvents.filter(e => e.event_type !== 'available' && parseISO(e.start_datetime).getHours() === hour);
                          const isAvailable = availableHours.length > 0;
                          
                          return (
                            <div
                              key={dayIndex}
                              className={`relative h-12 border-r last:border-r-0 ${
                                isSameDay(day, new Date()) ? 'bg-blue-50/30' : ''
                              } ${isAvailable ? 'bg-emerald-50' : ''}`}
                            >
                              {isAvailable && (
                                <div className="absolute inset-0 border-l-3 border-emerald-400 bg-emerald-100/60 flex items-center px-2">
                                  <span className="text-xs text-emerald-600 font-medium truncate">Available</span>
                                </div>
                              )}
                              {otherEvents.map((event, eventIndex) => {
                                const colorClasses = event.event_type === 'booked'
                                  ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-500'
                                  : event.event_type === 'personal'
                                  ? 'bg-amber-100 text-amber-700 border-l-4 border-amber-500'
                                  : event.event_type === 'travel'
                                  ? 'bg-purple-100 text-purple-700 border-l-4 border-purple-500'
                                  : event.event_type === 'preparation'
                                  ? 'bg-cyan-100 text-cyan-700 border-l-4 border-cyan-500'
                                  : 'bg-red-100 text-red-700 border-l-4 border-red-500';
                                return (
                                <div
                                  key={event.id}
                                  className={`absolute left-1 right-1 rounded px-2 py-1 text-xs font-medium truncate cursor-pointer group z-10 ${colorClasses}`}
                                  style={{ top: `${eventIndex * 24}px` }}
                                  title={event.title}
                                >
                                  {event.title}
                                  {!event.booking_id && !event.is_booking && (
                                    <button
                                      onClick={() => handleDeleteEvent(event.id)}
                                      className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-6 p-4 border-t bg-slate-50">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-400 rounded"></div>
                  <span className="text-sm text-slate-600">Available — open for bookings</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span className="text-sm text-slate-600">Booked — confirmed training session</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-amber-400 rounded"></div>
                  <span className="text-sm text-slate-600">Pending — awaiting your approval</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-400 rounded"></div>
                  <span className="text-sm text-slate-600">Travel — travel days</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-cyan-400 rounded"></div>
                  <span className="text-sm text-slate-600">Preparation — prep time</span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="space-y-4">
              {applications.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No applications yet</h3>
                  <p className="text-slate-500 mb-6">Start applying to training opportunities</p>
                  <Button onClick={() => navigate('/search?type=jobs')} className="bg-blue-600 hover:bg-blue-700 text-white">
                    Browse Requirements
                  </Button>
                </div>
              ) : (
                applications.map(app => (
                  <div key={app.id} className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-lg text-slate-900 mb-1">{app.job_title}</h4>
                        <p className="text-sm text-slate-500">Applied {new Date(app.applied_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                        app.status === 'pending'
                          ? 'bg-amber-50 text-amber-700'
                          : app.status === 'accepted'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => navigate(`/job/${app.job_id}`)}
                    >
                      View Job
                    </Button>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="space-y-4">
              {/* Pending Approval Section */}
              {bookings.filter(b => b.status === 'pending_approval').length > 0 && (
                <div className="mb-6">
                  <h3 className="font-outfit font-semibold text-lg text-amber-800 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Pending Approval ({bookings.filter(b => b.status === 'pending_approval').length})
                  </h3>
                  {bookings.filter(b => b.status === 'pending_approval').map(booking => (
                    <div key={booking.id} className="bg-amber-50 rounded-xl border border-amber-200 p-6 mb-3" data-testid={`pending-booking-${booking.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-lg text-slate-900 mb-1">{booking.job_title}</h4>
                          <p className="text-sm text-slate-500">Requested by {booking.corporate_name}</p>
                        </div>
                        <span className="px-3 py-1 text-xs rounded-full font-medium bg-amber-100 text-amber-800">
                          Awaiting Your Approval
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(booking.start_datetime).toLocaleDateString()}</span>
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{new Date(booking.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />₹{booking.amount?.toLocaleString()}</span>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/bookings/${booking.id}/accept`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Booking accepted!');
                              fetchData();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept'); }
                          }}
                          data-testid={`accept-booking-${booking.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/bookings/${booking.id}/reject`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Booking declined');
                              fetchData();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to decline'); }
                          }}
                          data-testid={`reject-booking-${booking.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Active & Completed Bookings */}
              {bookings.filter(b => b.status !== 'pending_approval' && b.status !== 'rejected').length === 0 && bookings.filter(b => b.status === 'pending_approval').length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No bookings yet</h3>
                  <p className="text-slate-500">Your confirmed training sessions will appear here</p>
                </div>
              ) : (
                bookings.filter(b => b.status !== 'pending_approval' && b.status !== 'rejected').map(booking => {
                  const tStatus = booking.training_status || 'confirmed';
                  const frontendUrl = window.location.origin;
                  const feedbackUrl = booking.feedback_link_id ? `${frontendUrl}/feedback/${booking.feedback_link_id}` : null;
                  
                  return (
                    <div key={booking.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`booking-${booking.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-lg text-slate-900 mb-1">{booking.job_title}</h4>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-slate-500">{booking.corporate_name}</p>
                            {booking.initiated_by === 'corporate' && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600 font-medium" data-testid={`booking-source-${booking.id}`}>
                                Company Initiated
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 text-xs rounded-full font-medium ${getTrainingStatusStyle(tStatus)}`} data-testid={`training-status-${booking.id}`}>
                            {getTrainingStatusLabel(tStatus)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(booking.start_datetime).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(booking.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ₹{booking.amount?.toLocaleString()}
                        </span>
                        {booking.feedback_count > 0 && (
                          <span className="flex items-center gap-1 text-purple-600">
                            <MessageSquare className="w-4 h-4" />
                            {booking.feedback_count} feedback(s)
                          </span>
                        )}
                      </div>

                      {/* Feedback Link Display */}
                      {feedbackUrl && (
                        <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-xs text-purple-600 font-medium mb-1">Feedback Link (share with participants):</p>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-white px-2 py-1 rounded border border-purple-200 flex-1 truncate" data-testid={`feedback-link-${booking.id}`}>
                              {feedbackUrl}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-purple-600 hover:text-purple-800"
                              onClick={() => {
                                navigator.clipboard.writeText(feedbackUrl);
                                toast.success('Feedback link copied!');
                              }}
                              data-testid={`copy-feedback-link-${booking.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                        <div className="flex gap-2">
                          {tStatus === 'confirmed' && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/bookings/${booking.id}/training-status?status=in_progress`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Training started!');
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start training'); }
                              }}
                              data-testid={`start-training-${booking.id}`}
                            >
                              <Play className="w-4 h-4 mr-1" /> Start Training
                            </Button>
                          )}
                          {(tStatus === 'in_progress' || tStatus === 'feedback_pending') && !feedbackUrl && (
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/bookings/${booking.id}/generate-feedback-link`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Feedback link generated!');
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate link'); }
                              }}
                              data-testid={`generate-feedback-${booking.id}`}
                            >
                              <Link2 className="w-4 h-4 mr-1" /> Generate Feedback Link
                            </Button>
                          )}
                          {(tStatus === 'in_progress' || tStatus === 'feedback_pending') && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/bookings/${booking.id}/training-status?status=completed`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Training marked as completed!');
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to complete training'); }
                              }}
                              data-testid={`complete-training-${booking.id}`}
                            >
                              <ClipboardCheck className="w-4 h-4 mr-1" /> Mark Complete
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setContractBooking(booking)}
                            data-testid={`view-contract-${booking.id}`}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            View Contract
                          </Button>
                          {tStatus === 'completed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={async () => {
                                const comment = window.prompt('How was your experience working with this company? (Leave a review)');
                                if (!comment) return;
                                const rating = window.prompt('Rate the company (1-5 stars):', '5');
                                const ratingNum = parseInt(rating);
                                if (!ratingNum || ratingNum < 1 || ratingNum > 5) { toast.error('Invalid rating (1-5)'); return; }
                                try {
                                  await axios.post(`${API}/company-reviews`, {
                                    corporate_id: booking.corporate_id,
                                    booking_id: booking.id,
                                    rating: ratingNum,
                                    comment
                                  }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('Company review submitted!');
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit review'); }
                              }}
                              data-testid={`review-company-${booking.id}`}
                            >
                              <Star className="w-4 h-4 mr-1" /> Review Company
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-outfit font-semibold text-xl text-slate-900">Profile Settings</h3>
                {!editingProfile ? (
                  <Button onClick={() => setEditingProfile(true)} variant="outline" data-testid="edit-profile-btn">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={() => setEditingProfile(false)} variant="outline">Cancel</Button>
                    <Button onClick={handleUpdateProfile} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-profile-btn">
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>

              {/* Profile Image */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
                <ProfileImageUpload
                  currentImage={profile?.avatar}
                  name={profile?.name}
                  size="lg"
                  onUploadSuccess={(path) => setProfile(prev => ({ ...prev, avatar: path }))}
                />
                <div>
                  <h4 className="font-semibold text-slate-900">{profile?.name}</h4>
                  <p className="text-sm text-slate-500">Click the image to upload a new photo</p>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP or GIF. Max 5MB.</p>
                </div>
              </div>

              {editingProfile ? (
                <div className="space-y-6">
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Tell companies about yourself..."
                      className="mt-2"
                      rows={4}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Experience (Years)</Label>
                      <Input
                        type="number"
                        value={profileForm.experience_years}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Hourly Rate (₹/hr)</Label>
                      <Input
                        type="number"
                        value={profileForm.hourly_rate}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Country</Label>
                      <select
                        value={selectedCountry}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="w-full h-10 px-3 mt-2 rounded-lg border border-slate-200"
                      >
                        <option value="">Select Country</option>
                        {Object.entries(getCountriesByRegion()).map(([region, countries]) => (
                          <optgroup key={region} label={`── ${region} ──`}>
                            {countries.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>City</Label>
                      <select
                        value={profileForm.location}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full h-10 px-3 mt-2 rounded-lg border border-slate-200"
                        disabled={!selectedCountry}
                      >
                        <option value="">{selectedCountry ? 'Select City' : 'Select Country First'}</option>
                        {getCitiesForCountry(selectedCountry).map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Skills - Multi-select dropdown */}
                  <div>
                    <Label>Skills (Select multiple)</Label>
                    <select
                      multiple
                      value={profileForm.skills}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setProfileForm(prev => ({ ...prev, skills: selected }));
                      }}
                      className="w-full h-32 px-3 mt-2 rounded-lg border border-slate-200"
                    >
                      {SKILLS.map(skill => (
                        <option key={skill} value={skill}>{skill}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple. Selected: {profileForm.skills.length}</p>
                  </div>

                  {/* Industry - Multi-select dropdown */}
                  <div>
                    <Label>Industries (Select multiple)</Label>
                    <select
                      multiple
                      value={profileForm.industry}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setProfileForm(prev => ({ ...prev, industry: selected }));
                      }}
                      className="w-full h-32 px-3 mt-2 rounded-lg border border-slate-200"
                    >
                      {INDUSTRIES.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple. Selected: {profileForm.industry.length}</p>
                  </div>

                  {/* 3-Tier Training Selection */}
                  <div>
                    <Label>Training Expertise - Add Specializations</Label>
                    <div className="mt-2 p-3 border rounded-lg bg-slate-50 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={trainingMainCat}
                          onChange={(e) => {
                            setTrainingMainCat(e.target.value);
                            setTrainingSubCat('');
                            setTrainingSubSubCat('');
                          }}
                          className="w-full h-9 px-2 text-sm rounded border border-slate-200"
                        >
                          <option value="">1. Main Category</option>
                          {getMainCategories().map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                        <select
                          value={trainingSubCat}
                          onChange={(e) => {
                            setTrainingSubCat(e.target.value);
                            setTrainingSubSubCat('');
                          }}
                          className="w-full h-9 px-2 text-sm rounded border border-slate-200"
                          disabled={!trainingMainCat}
                        >
                          <option value="">2. Sub Category</option>
                          {getSubCategories(trainingMainCat).map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={trainingSubSubCat}
                          onChange={(e) => setTrainingSubSubCat(e.target.value)}
                          className="w-full h-9 px-2 text-sm rounded border border-slate-200"
                          disabled={!trainingSubCat}
                        >
                          <option value="">3. Training Area</option>
                          {getSubSubCategories(trainingMainCat, trainingSubCat).map(subSub => (
                            <option key={subSub} value={subSub}>{subSub}</option>
                          ))}
                        </select>
                        <select
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val && !profileForm.training_types.includes(val)) {
                              setProfileForm(prev => ({
                                ...prev,
                                training_types: [...prev.training_types, val]
                              }));
                            }
                            e.target.value = '';
                          }}
                          className="w-full h-9 px-2 text-sm rounded border border-slate-200"
                          disabled={!trainingSubSubCat}
                        >
                          <option value="">4. Add Training →</option>
                          {getTrainingTypes(trainingMainCat, trainingSubCat, trainingSubSubCat).map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Selected trainings display */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {profileForm.training_types.map(type => (
                        <span key={type} className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {type}
                          <button
                            onClick={() => setProfileForm(prev => ({
                              ...prev,
                              training_types: prev.training_types.filter(t => t !== type)
                            }))}
                            className="ml-1 hover:text-purple-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Selected: {profileForm.training_types.length} training types</p>
                  </div>

                  {/* Languages - Multi-select dropdown */}
                  <div>
                    <Label>Languages (Training Delivery)</Label>
                    <select
                      multiple
                      value={profileForm.languages}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setProfileForm(prev => ({ ...prev, languages: selected }));
                      }}
                      className="w-full h-32 px-3 mt-2 rounded-lg border border-slate-200"
                    >
                      {LANGUAGES.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple. English is auto-selected. Selected: {profileForm.languages.length}</p>
                  </div>

                  <div>
                    <Label>Qualifications (comma separated)</Label>
                    <Input
                      value={profileForm.qualifications.join(', ')}
                      onChange={(e) => setProfileForm(prev => ({
                        ...prev,
                        qualifications: e.target.value.split(',').map(q => q.trim()).filter(Boolean)
                      }))}
                      placeholder="MBA, PhD, etc."
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>Certifications (comma separated)</Label>
                    <Input
                      value={profileForm.certifications.join(', ')}
                      onChange={(e) => setProfileForm(prev => ({
                        ...prev,
                        certifications: e.target.value.split(',').map(c => c.trim()).filter(Boolean)
                      }))}
                      placeholder="PMP, Six Sigma, etc."
                      className="mt-2"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Bio</h4>
                    <p className="text-slate-900">{profile?.bio || 'No bio added yet'}</p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-2">Experience</h4>
                      <p className="text-slate-900">{profile?.experience_years || 0} years</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-2">Hourly Rate</h4>
                      <p className="text-slate-900">₹{profile?.hourly_rate || 0}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-2">Location</h4>
                      <p className="text-slate-900">{profile?.location || 'Not set'}</p>
                    </div>
                  </div>

                  {profile?.skills?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-2">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {profile.skills.map(skill => (
                          <span key={skill} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {profile?.industry?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-2">Industries</h4>
                      <div className="flex flex-wrap gap-2">
                        {profile.industry.map(ind => (
                          <span key={ind} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm">
                            {ind}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {profile?.training_types?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-500 mb-2">Training Types</h4>
                      <div className="flex flex-wrap gap-2">
                        {profile.training_types.map(type => (
                          <span key={type} className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Interviews Tab */}
          <TabsContent value="interviews">
            <div className="space-y-4">
              {interviews.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No interviews yet</h3>
                  <p className="text-slate-500">When companies schedule interviews with you, they&apos;ll appear here.</p>
                </div>
              ) : (
                interviews.map(interview => (
                  <div key={interview.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`interview-${interview.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-1">{interview.job_title}</h3>
                        <p className="text-sm text-slate-500 mb-3">with {interview.corporate_name}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 text-blue-500" />
                            {new Date(interview.proposed_datetime).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-blue-500" />
                            {new Date(interview.proposed_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} ({interview.duration_minutes} min)
                          </span>
                        </div>
                        {interview.notes && <p className="text-sm text-slate-500 mt-2">{interview.notes}</p>}
                        {interview.meet_link && (
                          <a
                            href={interview.meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                            data-testid="join-meet-link"
                          >
                            <Video className="w-4 h-4" />
                            Join Google Meet
                          </a>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getInterviewStatusStyle(interview.status)}`}>
                          {interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
                        </span>
                        {interview.status === 'pending' && (
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/interviews/${interview.id}/respond?status=accepted`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Interview accepted!');
                                  fetchData();
                                } catch { toast.error('Failed to respond'); }
                              }}
                              data-testid="accept-interview-btn"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/interviews/${interview.id}/respond?status=declined`, {}, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success('Interview declined');
                                  fetchData();
                                } catch { toast.error('Failed to respond'); }
                              }}
                              data-testid="decline-interview-btn"
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Decline
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6" data-testid="analytics-dashboard">
              <h2 className="font-outfit text-xl font-semibold text-slate-900">Performance Analytics</h2>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{bookings.length}</p>
                  <p className="text-xs text-slate-500 mt-1">Total Bookings</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(bookings.reduce((sum, b) => sum + (b.amount || 0), 0))}</p>
                  <p className="text-xs text-slate-500 mt-1">Total Earnings</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{stats?.rating?.toFixed(1) || '0.0'}</p>
                  <p className="text-xs text-slate-500 mt-1">Avg Rating</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{analyticsData.totalApplications || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Applications Sent</p>
                </div>
              </div>

              {/* Monthly Bookings Chart */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-slate-900 mb-4">Monthly Bookings (Last 6 Months)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.monthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                        formatter={(value) => [value, 'Bookings']}
                      />
                      <Bar dataKey="bookings" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Monthly Earnings Chart */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-outfit font-semibold text-slate-900 mb-4">Monthly Earnings (Last 6 Months)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData.monthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                        formatter={(value) => [formatCurrency(value), 'Earnings']}
                      />
                      <Line type="monotone" dataKey="earnings" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Skills Distribution */}
              {analyticsData.skills?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-outfit font-semibold text-slate-900 mb-4">Your Skills</h3>
                  <div className="h-64 flex justify-center">
                    <ResponsiveContainer width="100%" height="100%" maxWidth={400}>
                      <PieChart>
                        <Pie
                          data={analyticsData.skills}
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          innerRadius={50}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name }) => name}
                        >
                          {analyticsData.skills.map((entry, index) => (
                            <Cell key={entry.name} fill={['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1'][index % 8]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <div className="space-y-6" data-testid="availability-calendar">
              <div className="flex justify-between items-center">
                <h2 className="font-outfit text-xl font-semibold text-slate-900">Weekly Availability</h2>
                <Button
                  onClick={() => setShowAddSlot(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="add-availability-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Slot
                </Button>
              </div>

              <p className="text-sm text-slate-500">Set your recurring weekly availability so companies know when you&apos;re free for trainings.</p>

              <div className="grid gap-3">
                {DAY_NAMES.map((day, idx) => {
                  const daySlots = availabilitySlots.filter(s => s.day_of_week === idx);
                  return (
                    <div key={day} className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-slate-900">{day}</h3>
                        {daySlots.length === 0 ? (
                          <span className="text-xs text-slate-400">No slots set</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {daySlots.map(slot => (
                              <span key={slot.id} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-full">
                                {slot.start_time} — {slot.end_time}
                                <button onClick={() => handleDeleteSlot(slot.id)} className="text-red-400 hover:text-red-600">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add Slot Dialog */}
              <Dialog open={showAddSlot} onOpenChange={setShowAddSlot}>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-xl">Add Availability Slot</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Day of Week</Label>
                      <select
                        value={slotForm.day_of_week}
                        onChange={(e) => setSlotForm(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 mt-1"
                        data-testid="slot-day-select"
                      >
                        {DAY_NAMES.map((day, i) => (
                          <option key={i} value={i}>{day}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">From</Label>
                        <Input
                          type="time"
                          value={slotForm.start_time}
                          onChange={(e) => setSlotForm(prev => ({ ...prev, start_time: e.target.value }))}
                          className="mt-1"
                          data-testid="slot-start-time"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">To</Label>
                        <Input
                          type="time"
                          value={slotForm.end_time}
                          onChange={(e) => setSlotForm(prev => ({ ...prev, end_time: e.target.value }))}
                          className="mt-1"
                          data-testid="slot-end-time"
                        />
                      </div>
                    </div>
                    <Button onClick={handleAddAvailabilitySlot} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-slot-btn">
                      Save Slot
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <div className="space-y-6" data-testid="portfolio-section">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-outfit font-semibold text-xl text-slate-900">My Portfolio</h3>
                  <p className="text-sm text-slate-500 mt-1">Showcase your work samples, certificates, and training materials</p>
                </div>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.ppt,.pptx" onChange={handlePortfolioUpload} data-testid="portfolio-upload-input" />
                  <div className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    <Plus className="w-4 h-4 mr-2" />
                    Upload Item
                  </div>
                </label>
              </div>

              {portfolioItems.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-2">No portfolio items yet</h4>
                  <p className="text-slate-500 mb-6">Upload certificates, work samples, or training materials to showcase your expertise</p>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.ppt,.pptx" onChange={handlePortfolioUpload} />
                    <span className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                      <Plus className="w-4 h-4 mr-2" />
                      Upload Your First Item
                    </span>
                  </label>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {portfolioItems.map(item => (
                    <div key={item.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden group hover:shadow-md transition-shadow" data-testid={`portfolio-item-${item.id}`}>
                      {item.file_type?.startsWith('image/') ? (
                        <div className="h-40 bg-slate-100 overflow-hidden">
                          <img src={item.file_path} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                      ) : (
                        <div className="h-40 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                          <FileText className="w-12 h-12 text-slate-400" />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-slate-900 truncate">{item.title}</h5>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                item.item_type === 'certificate' ? 'bg-amber-50 text-amber-700' :
                                item.item_type === 'sample' ? 'bg-blue-50 text-blue-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {item.item_type}
                              </span>
                              <span className="text-xs text-slate-400">{item.file_name}</span>
                            </div>
                            {item.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.description}</p>}
                            <p className="text-xs text-slate-400 mt-2">{new Date(item.created_at).toLocaleDateString()}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeletePortfolioItem(item.id)}
                            data-testid={`delete-portfolio-${item.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 text-center">{portfolioItems.length} / 20 items uploaded. Supported: Images, PDFs, Word docs, PowerPoints (max 10MB each)</p>
            </div>
          </TabsContent>

          {/* Notification Preferences Tab */}
          <TabsContent value="notifications">
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-testid="notification-prefs-section">
              <div>
                <h3 className="font-outfit font-semibold text-xl text-slate-900">Alert Preferences</h3>
                <p className="text-sm text-slate-500 mt-1">Control which notifications you receive and filter job alerts to match your preferences</p>
              </div>

              {notifPrefs ? (
                <>
                  {/* Master toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <div>
                      <h4 className="font-medium text-slate-900">Enable All Notifications</h4>
                      <p className="text-xs text-slate-500">Master toggle for all platform alerts</p>
                    </div>
                    <button
                      onClick={() => setNotifPrefs(p => ({ ...p, enabled: !p.enabled }))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${notifPrefs.enabled ? 'bg-blue-600' : 'bg-slate-300'}`}
                      data-testid="notif-master-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${notifPrefs.enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {/* Category toggles */}
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      { key: 'job_match_alerts', label: 'Job Match Alerts', desc: 'When new jobs match your skills' },
                      { key: 'interview_alerts', label: 'Interview Alerts', desc: 'When you get interview invites' },
                      { key: 'booking_alerts', label: 'Booking Alerts', desc: 'When you get bookings & updates' },
                    ].map(item => (
                      <div key={item.key} className="p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <h5 className="text-sm font-medium text-slate-900">{item.label}</h5>
                          <button
                            onClick={() => setNotifPrefs(p => ({ ...p, [item.key]: !p[item.key] }))}
                            className={`relative w-9 h-5 rounded-full transition-colors ${notifPrefs[item.key] ? 'bg-blue-600' : 'bg-slate-300'}`}
                            data-testid={`toggle-${item.key}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${notifPrefs[item.key] ? 'translate-x-4' : ''}`} />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* Job Alert Filters */}
                  <div className="border-t border-slate-200 pt-6">
                    <h4 className="font-medium text-slate-900 mb-1">Job Alert Filters</h4>
                    <p className="text-xs text-slate-500 mb-4">Only receive job match alerts that meet these criteria</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Minimum Budget (₹)</Label>
                        <Input
                          type="number"
                          value={notifPrefs.min_budget || ''}
                          onChange={(e) => setNotifPrefs(p => ({ ...p, min_budget: e.target.value ? parseFloat(e.target.value) : null }))}
                          placeholder="No minimum"
                          className="mt-1"
                          data-testid="notif-min-budget"
                        />
                      </div>
                      <div>
                        <Label>Preferred Locations (comma-separated)</Label>
                        <Input
                          value={(notifPrefs.preferred_locations || []).join(', ')}
                          onChange={(e) => setNotifPrefs(p => ({ ...p, preferred_locations: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                          placeholder="Mumbai, Delhi, Bangalore"
                          className="mt-1"
                          data-testid="notif-locations"
                        />
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSaveNotifPrefs} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-notif-prefs-btn">
                    Save Preferences
                  </Button>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">Loading preferences...</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Training Contract Modal */}
      {contractBooking && (
        <TrainingContract 
          booking={contractBooking} 
          onClose={() => setContractBooking(null)} 
        />
      )}
    </div>
  );
}
